/**
 * Flinch — being shot disturbs your aim.
 *
 * This is the half of a gunfight that decides who wins a trade. Without it, two
 * players holding an angle on each other is a pure reaction-time contest and the
 * first bullet buys the shooter nothing; with it, landing first means the other
 * one's crosshair leaves your chest and they have to drag it back.
 *
 * Two rules define it, and both matter:
 *
 * 1. **It moves the point of aim.** The camera rig's trauma shake does not — that
 *    is a deliberate, well-documented split there, and it is why guns in this game
 *    feel learnable. Flinch has to be the other kind: it goes through the same
 *    servo the recoil pattern uses, so the sight picture and the bullet agree.
 * 2. **It decays like recoil, not like a shake.** A snap to the punch, then an
 *    exponential return. No hold: unlike recoil there is nothing to accumulate
 *    toward, and a hold would make a burst of chip damage unrecoverable.
 *
 * The policy — how big the punch is, which way it goes, how much resistance takes
 * off — lives here. The integration lives in `CameraRig`, which owns the aim
 * accumulators. They meet at `player:flinch`.
 */

import * as THREE from 'three';

const DEG = Math.PI / 180;

export const TUNING = {
  /**
   * Degrees of upward punch per point of damage. A 13-point rifle round from a
   * hostile is ~1.3 degrees, which is about a third of a body width at 30 m —
   * enough to break a sight picture, not enough to lose the target.
   */
  degPerDamage: 0.13,
  /** Ceiling on the *base* magnitude of one hit, degrees, before resistance. */
  maxDeg: 4.2,
  /**
   * Absolute ceiling after resistance, degrees. Negative resistance amplifies, and
   * without a second ceiling a heavy hit on a build that has sold its flinch
   * resistance would throw the aim further than the player can recover inside a
   * gunfight. 6.5 degrees is roughly a body width at 15 m: recoverable, but it
   * ends the exchange you were in.
   */
  hardMaxDeg: 6.5,
  /** Sublinear: two 20s should flinch more in total than one 40. */
  damageExponent: 0.85,
  /** Horizontal punch as a fraction of the vertical, before the direction term. */
  yawShare: 0.55,
  /** Unbiased jitter as a fraction of the magnitude, so repeat hits are not a
   *  memorised shape the way a recoil pattern deliberately is. */
  random: 0.35,
  /**
   * Resistance range.
   *
   * The top is 0.85: immunity is not a thing an attachment may sell, and a build
   * that cannot be disturbed at all removes the mechanic for everyone shooting at
   * it. The bottom is *negative*, because that is how a conversion pays for
   * itself — a quick-ADS sniper mastery buys its speed with −0.25, meaning 25 %
   * more aim punch than stock. Clamping that to zero, as this did, is exactly the
   * failure this project keeps hitting: a downside that reads as configured and
   * costs nothing, which quietly turns a trade into an upgrade.
   *
   * −1 is the floor: double the stock punch. Below that a single graze walks the
   * sight off a torso at any range worth taking the shot at, and there is no
   * benefit worth pricing at "cannot fight back".
   */
  maxResist: 0.85,
  minResist: -1,
  /** Punches inside this window stack at reduced weight, so a 900 RPM burst does
   *  not multiply into a backflip. */
  stackWindow: 0.35,
  stackFalloff: 0.55,
};

const _fwd = new THREE.Vector3();
const _right = new THREE.Vector3();

export class Flinch {
  constructor(ctx, system) {
    this.ctx = ctx;
    this.system = system;
    this.count = 0;
    this.lastPitchDeg = 0;
    this.lastYawDeg = 0;
    this._lastAt = -99;
    this._stack = 0;

    this._off = ctx.events.on('player:damage', (e) => this.onDamage(e));
  }

  onDamage(e) {
    const amount = e?.amount;
    if (!Number.isFinite(amount) || amount <= 0) return;
    this.punch(amount, e.dir ?? null);
  }

  /**
   * Apply an aim punch. Exposed so the test harness can fire one without needing a
   * hostile to actually shoot the player.
   * @param {number} damage points of damage
   * @param {THREE.Vector3|null} dir direction the round was travelling
   */
  punch(damage, dir) {
    const cfg = this.system.config;
    const resist = clamp(cfg.flinchResist ?? 0, TUNING.minResist, TUNING.maxResist);

    let deg = TUNING.degPerDamage * Math.pow(damage, TUNING.damageExponent);
    // Two ceilings, and they are not the same one. `maxDeg` bounds what a single
    // round is worth *before* the build is taken into account, so a shotgun slug
    // and a rifle round converge; `hardMaxDeg` bounds what actually reaches the
    // camera, so amplification from a negative resist cannot run away.
    deg = Math.min(deg, TUNING.maxDeg) * (1 - resist);
    deg = Math.min(deg, TUNING.hardMaxDeg);
    if (deg <= 1e-4) {
      // Still publish, so a fully-resistant build is visibly *doing* something
      // rather than looking like the event never fired.
      this._publish(0, 0, damage, resist);
      return;
    }

    // Rapid hits stack, but with diminishing weight.
    const now = this.ctx.engine?.elapsed ?? 0;
    this._stack = now - this._lastAt < TUNING.stackWindow ? this._stack * TUNING.stackFalloff : 1;
    this._lastAt = now;
    deg *= this._stack;

    // Vertical is always up: a hit drives the muzzle skyward, which is the part the
    // player has to fight down.
    let pitch = deg;
    // Horizontal follows the round. A bullet travelling to your right (fired from
    // your left) shoves the muzzle right; camera yaw increases to the *left*, so
    // the sign is inverted here.
    let yaw = 0;
    const p = this.ctx.player;
    if (dir && p) {
      _fwd.set(-Math.sin(p.yaw), 0, -Math.cos(p.yaw));
      _right.set(Math.cos(p.yaw), 0, -Math.sin(p.yaw));
      const lateral = clamp(dir.x * _right.x + dir.z * _right.z, -1, 1);
      yaw = -lateral * deg * TUNING.yawShare;
      void _fwd;
    }
    const jitter = () => (Math.random() * 2 - 1) * TUNING.random;
    pitch *= 1 + jitter();
    yaw += deg * TUNING.yawShare * jitter();

    this.count++;
    this.lastPitchDeg = +pitch.toFixed(3);
    this.lastYawDeg = +yaw.toFixed(3);
    this._publish(pitch * DEG, yaw * DEG, damage, resist);
  }

  _publish(pitch, yaw, damage, resist) {
    this.ctx.events.emit('player:flinch', { pitch, yaw, damage, resist });
  }

  report() {
    const rig = this.ctx.cameraRig;
    return {
      punches: this.count,
      lastPitchDeg: this.lastPitchDeg,
      lastYawDeg: this.lastYawDeg,
      // Read straight back off the rig: this is the number that is actually
      // steering the camera this frame, not a copy of what we asked for.
      liveDeg: rig ? +(Math.hypot(rig.flinchPitch ?? 0, rig.flinchYaw ?? 0) / DEG).toFixed(3) : null,
      wired: !!(this.ctx.cameraRig && 'flinchPitch' in this.ctx.cameraRig),
    };
  }

  dispose() {
    this._off?.();
  }
}

function clamp(v, a, b) {
  return v < a ? a : v > b ? b : v;
}
